import React, { useState, useEffect } from "react";
import { db, auth } from "../../firebase";
import { useAuthState } from "react-firebase-hooks/auth";
import {
  doc,
  getDoc,
  updateDoc,
} from "firebase/firestore";
import { useNavigate, useParams } from "react-router-dom";

function EditTeamsPage() {
  const { teamId } = useParams();
  const [user] = useAuthState(auth);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [notification, setNotification] = useState({ show: false, message: "", type: "" });

  // Team data state
  const [team, setTeam] = useState({
    name: "",
    sport: "",
    ageGroup: "",
    teamType: "Recreational",
    season: "",
    country: "United States",
    state: "",
    city: "",
    zipCode: "",
    timeZone: "",
    wins: 0,
    losses: 0,
    description: "",
    division: "",
  });

  // Options for dropdown selects
  const sportOptions = [
    "Basketball", "Soccer", "Hockey", "Baseball", "Volleyball", 
    "Football", "Tennis", "Swimming", "Track & Field", "Golf", 
    "Lacrosse", "Rugby", "Cricket", "Softball", "Other"
  ];

  const ageGroupOptions = [
    "U6", "U8", "U10", "U12", "U14", "U16", "U18", "U21", "Adult", "Senior"
  ];

  const teamTypeOptions = [
    "Recreational", "Competitive", "Elite", "School", "Club", "Community", "Travel"
  ];

  const seasonOptions = [
    "Spring 2025", "Summer 2025", "Fall 2025", "Winter 2025", "Year-Round 2025"
  ];

  const countryOptions = [
    "United States", "Canada", "United Kingdom", "Australia", "Germany", 
    "France", "Spain", "Italy", "Japan", "China", "Brazil", "Mexico", "Other"
  ];

  const stateOptions = [
    "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", 
    "Connecticut", "Delaware", "Florida", "Georgia", "Hawaii", "Idaho", 
    "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana", 
    "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota", 
    "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", 
    "New Hampshire", "New Jersey", "New Mexico", "New York", 
    "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon", 
    "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota", 
    "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", 
    "West Virginia", "Wisconsin", "Wyoming", "District of Columbia",
    // Canadian provinces
    "Alberta", "British Columbia", "Manitoba", "New Brunswick", 
    "Newfoundland and Labrador", "Nova Scotia", "Ontario", 
    "Prince Edward Island", "Quebec", "Saskatchewan"
  ];

  const timeZoneOptions = [
    "Pacific Time (PT)", "Mountain Time (MT)", "Central Time (CT)", 
    "Eastern Time (ET)", "Atlantic Time (AT)", "Greenwich Mean Time (GMT)", 
    "Central European Time (CET)", "Eastern European Time (EET)", 
    "Japan Standard Time (JST)", "Australian Eastern Time (AET)"
  ];

  // Fetch team data
  useEffect(() => {
    const fetchTeam = async () => {
      if (!user || !teamId) {
        setLoading(false);
        return;
      }

      try {
        const teamDoc = await getDoc(doc(db, "teams", teamId));
        
        if (teamDoc.exists()) {
          const teamData = teamDoc.data();
          
          // Verify user is the coach
          if (teamData.coachId !== user.uid) {
            setNotification({
              show: true,
              message: "You don't have permission to edit this team.",
              type: "error"
            });
            setTimeout(() => navigate("/my-teams"), 3000);
            return;
          }
          
          setTeam({
            name: teamData.name || "",
            sport: teamData.sport || "",
            ageGroup: teamData.ageGroup || "",
            teamType: teamData.teamType || "Recreational",
            season: teamData.season || "",
            country: teamData.country || "United States",
            state: teamData.state || "",
            city: teamData.city || "",
            zipCode: teamData.zipCode || "",
            timeZone: teamData.timeZone || "",
            wins: teamData.wins || 0,
            losses: teamData.losses || 0,
            description: teamData.description || "",
            division: teamData.division || "",
          });
        } else {
          setNotification({
            show: true,
            message: "Team not found.",
            type: "error"
          });
          setTimeout(() => navigate("/my-teams"), 3000);
        }
      } catch (error) {
        console.error("Error fetching team:", error);
        setNotification({
          show: true,
          message: "Error loading team data. Please try again.",
          type: "error"
        });
      }
      
      setLoading(false);
    };

    fetchTeam();
  }, [user, teamId, navigate]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setTeam(prevTeam => ({
      ...prevTeam,
      [name]: value
    }));
    
    // Clear validation error when field is modified
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  // Handle numeric input changes
  const handleNumericChange = (e) => {
    const { name, value } = e.target;
    const numericValue = value === "" ? 0 : parseInt(value, 10);
    
    if (!isNaN(numericValue)) {
      setTeam(prevTeam => ({
        ...prevTeam,
        [name]: numericValue
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};
    
    if (!team.name.trim()) errors.name = "Team name is required";
    if (!team.sport) errors.sport = "Sport is required";
    if (!team.season) errors.season = "Season is required";
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Save team data
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      // Scroll to the first error
      const firstErrorField = Object.keys(validationErrors)[0];
      const errorElement = document.getElementById(firstErrorField);
      if (errorElement) errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    
    setSaving(true);
    
    try {
      // Update team document
      await updateDoc(doc(db, "teams", teamId), {
        ...team,
        updatedAt: new Date(),
      });
      
      setNotification({
        show: true,
        message: "Team updated successfully!",
        type: "success"
      });
      
      setTimeout(() => navigate("/my-teams"), 2000);
    } catch (error) {
      console.error("Error updating team:", error);
      setNotification({
        show: true,
        message: "Failed to update team. Please try again.",
        type: "error"
      });
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container" style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        flexDirection: "column",
        gap: "1rem"
      }}>
        <div className="spinner" style={{
          width: "40px",
          height: "40px",
          border: "4px solid rgba(0, 0, 0, 0.1)",
          borderLeft: "4px solid #1976D2",
          borderRadius: "50%",
          animation: "spin 1s linear infinite"
        }}></div>
        <p style={{ color: "#555", fontSize: "1rem" }}>Loading team data...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (!user) {
    return (
      <div style={{
        maxWidth: "600px",
        margin: "4rem auto",
        padding: "2rem",
        textAlign: "center",
        background: "#fff",
        borderRadius: "10px",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)"
      }}>
        <h2 style={{ color: "#333", marginBottom: "1rem" }}>Access Restricted</h2>
        <p style={{ color: "#555", marginBottom: "2rem" }}>Please log in to edit team information.</p>
        <button
          style={{
            background: "linear-gradient(135deg, #1976D2, #0D47A1)",
            color: "#fff",
            border: "none",
            borderRadius: "5px",
            padding: "0.75rem 1.5rem",
            fontSize: "1rem",
            cursor: "pointer",
            transition: "transform 0.2s, background 0.3s",
          }}
          onClick={() => navigate("/login")}
        >
          Go to Login
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        maxWidth: "900px",
        margin: "2rem auto",
        padding: "2rem",
        fontFamily: "'Roboto', sans-serif",
        background: "#fff",
        borderRadius: "15px",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
      }}
    >
      <h2
        style={{
          textAlign: "center",
          marginBottom: "2rem",
          color: "#0D47A1",
          fontSize: "2rem",
          fontWeight: "500",
        }}
      >
        Edit Team
      </h2>

      {/* Notification */}
      {notification.show && (
        <div 
          style={{
            padding: "1rem",
            borderRadius: "5px",
            marginBottom: "1.5rem",
            backgroundColor: notification.type === "success" ? "#e8f5e9" : "#ffebee",
            color: notification.type === "success" ? "#2e7d32" : "#d32f2f",
            border: `1px solid ${notification.type === "success" ? "#a5d6a7" : "#ef9a9a"}`,
          }}
        >
          {notification.message}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Basic Team Information Section */}
        <div 
          style={{
            background: "#f5f9ff",
            padding: "1.5rem",
            borderRadius: "10px",
            marginBottom: "2rem",
            border: "1px solid #e3f2fd",
          }}
        >
          <h3 style={{ color: "#1976D2", marginTop: 0, marginBottom: "1.5rem" }}>
            Basic Team Information
          </h3>
          
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.5rem" }}>
            {/* Team Name */}
            <div style={{ gridColumn: "1 / 3" }}>
              <label 
                htmlFor="name"
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  color: "#444",
                  fontSize: "0.95rem",
                  fontWeight: "500",
                }}
              >
                Team Name <span style={{ color: "#d32f2f" }}>*</span>
              </label>
              <input
                id="name"
                type="text"
                name="name"
                value={team.name}
                onChange={handleChange}
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  fontSize: "1rem",
                  border: validationErrors.name ? "1px solid #d32f2f" : "1px solid #e0e0e0",
                  borderRadius: "5px",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05) inset",
                }}
                placeholder="e.g., Oakville Raptors"
              />
              {validationErrors.name && (
                <p style={{ color: "#d32f2f", fontSize: "0.85rem", marginTop: "0.5rem", marginBottom: 0 }}>
                  {validationErrors.name}
                </p>
              )}
            </div>

            {/* Sport */}
            <div>
              <label 
                htmlFor="sport"
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  color: "#444",
                  fontSize: "0.95rem",
                  fontWeight: "500",
                }}
              >
                Sport <span style={{ color: "#d32f2f" }}>*</span>
              </label>
              <select
                id="sport"
                name="sport"
                value={team.sport}
                onChange={handleChange}
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  fontSize: "1rem",
                  border: validationErrors.sport ? "1px solid #d32f2f" : "1px solid #e0e0e0",
                  borderRadius: "5px",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05) inset",
                  backgroundColor: "#fff",
                }}
              >
                <option value="">Select a sport</option>
                {sportOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
              {validationErrors.sport && (
                <p style={{ color: "#d32f2f", fontSize: "0.85rem", marginTop: "0.5rem", marginBottom: 0 }}>
                  {validationErrors.sport}
                </p>
              )}
            </div>

            {/* Age Group */}
            <div>
              <label 
                htmlFor="ageGroup"
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  color: "#444",
                  fontSize: "0.95rem",
                  fontWeight: "500",
                }}
              >
                Age Group
              </label>
              <select
                id="ageGroup"
                name="ageGroup"
                value={team.ageGroup}
                onChange={handleChange}
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  fontSize: "1rem",
                  border: "1px solid #e0e0e0",
                  borderRadius: "5px",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05) inset",
                  backgroundColor: "#fff",
                }}
              >
                <option value="">Select age group</option>
                {ageGroupOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>

            {/* Team Type */}
            <div>
              <label 
                htmlFor="teamType"
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  color: "#444",
                  fontSize: "0.95rem",
                  fontWeight: "500",
                }}
              >
                Team Type
              </label>
              <select
                id="teamType"
                name="teamType"
                value={team.teamType}
                onChange={handleChange}
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  fontSize: "1rem",
                  border: "1px solid #e0e0e0",
                  borderRadius: "5px",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05) inset",
                  backgroundColor: "#fff",
                }}
              >
                {teamTypeOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>

            {/* Season/Year */}
            <div>
              <label 
                htmlFor="season"
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  color: "#444",
                  fontSize: "0.95rem",
                  fontWeight: "500",
                }}
              >
                Season/Year <span style={{ color: "#d32f2f" }}>*</span>
              </label>
              <select
                id="season"
                name="season"
                value={team.season}
                onChange={handleChange}
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  fontSize: "1rem",
                  border: validationErrors.season ? "1px solid #d32f2f" : "1px solid #e0e0e0",
                  borderRadius: "5px",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05) inset",
                  backgroundColor: "#fff",
                }}
              >
                <option value="">Select season</option>
                {seasonOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
              {validationErrors.season && (
                <p style={{ color: "#d32f2f", fontSize: "0.85rem", marginTop: "0.5rem", marginBottom: 0 }}>
                  {validationErrors.season}
                </p>
              )}
            </div>

            {/* Team Stats */}
            <div>
              <label 
                htmlFor="wins"
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  color: "#444",
                  fontSize: "0.95rem",
                  fontWeight: "500",
                }}
              >
                Wins
              </label>
              <input
                id="wins"
                type="number"
                name="wins"
                min="0"
                value={team.wins}
                onChange={handleNumericChange}
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  fontSize: "1rem",
                  border: "1px solid #e0e0e0",
                  borderRadius: "5px",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05) inset",
                }}
              />
            </div>

            <div>
              <label 
                htmlFor="losses"
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  color: "#444",
                  fontSize: "0.95rem",
                  fontWeight: "500",
                }}
              >
                Losses
              </label>
              <input
                id="losses"
                type="number"
                name="losses"
                min="0"
                value={team.losses}
                onChange={handleNumericChange}
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  fontSize: "1rem",
                  border: "1px solid #e0e0e0",
                  borderRadius: "5px",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05) inset",
                }}
              />
            </div>

            {/* Division */}
            <div>
              <label 
                htmlFor="division"
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  color: "#444",
                  fontSize: "0.95rem",
                  fontWeight: "500",
                }}
              >
                Division
              </label>
              <input
                id="division"
                type="text"
                name="division"
                value={team.division}
                onChange={handleChange}
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  fontSize: "1rem",
                  border: "1px solid #e0e0e0",
                  borderRadius: "5px",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05) inset",
                }}
                placeholder="e.g., North Division, A Division"
              />
            </div>

            {/* Team Description */}
            <div style={{ gridColumn: "1 / 3" }}>
              <label 
                htmlFor="description"
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  color: "#444",
                  fontSize: "0.95rem",
                  fontWeight: "500",
                }}
              >
                Team Description
              </label>
              <textarea
                id="description"
                name="description"
                value={team.description}
                onChange={handleChange}
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  fontSize: "1rem",
                  border: "1px solid #e0e0e0",
                  borderRadius: "5px",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05) inset",
                  minHeight: "100px",
                  resize: "vertical",
                }}
                placeholder="Brief description of your team..."
              />
            </div>
          </div>
        </div>

        {/* Location & Time Zone Section */}
        <div 
          style={{
            background: "#f9f9f9",
            padding: "1.5rem",
            borderRadius: "10px",
            marginBottom: "2rem",
            border: "1px solid #f0f0f0",
          }}
        >
          <h3 style={{ color: "#555", marginTop: 0, marginBottom: "1.5rem" }}>
            Location & Time Zone
          </h3>
          
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.5rem" }}>
            {/* Country */}
            <div>
              <label 
                htmlFor="country"
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  color: "#444",
                  fontSize: "0.95rem",
                  fontWeight: "500",
                }}
              >
                Country
              </label>
              <select
                id="country"
                name="country"
                value={team.country}
                onChange={handleChange}
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  fontSize: "1rem",
                  border: "1px solid #e0e0e0",
                  borderRadius: "5px",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05) inset",
                  backgroundColor: "#fff",
                }}
              >
                {countryOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>

            {/* State/Province */}
            <div>
              <label 
                htmlFor="state"
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  color: "#444",
                  fontSize: "0.95rem",
                  fontWeight: "500",
                }}
              >
                State/Province
              </label>
              <select
                id="state"
                name="state"
                value={team.state}
                onChange={handleChange}
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  fontSize: "1rem",
                  border: "1px solid #e0e0e0",
                  borderRadius: "5px",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05) inset",
                  backgroundColor: "#fff",
                }}
                disabled={team.country !== "United States" && team.country !== "Canada"}
              >
                <option value="">Select state/province</option>
                {stateOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>

            {/* City */}
            <div>
              <label 
                htmlFor="city"
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  color: "#444",
                  fontSize: "0.95rem",
                  fontWeight: "500",
                }}
              >
                City
              </label>
              <input
                id="city"
                type="text"
                name="city"
                value={team.city}
                onChange={handleChange}
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  fontSize: "1rem",
                  border: "1px solid #e0e0e0",
                  borderRadius: "5px",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05) inset",
                }}
                placeholder="e.g., Oakville"
              />
            </div>

            {/* Zip/Postal Code */}
            <div>
              <label 
                htmlFor="zipCode"
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  color: "#444",
                  fontSize: "0.95rem",
                  fontWeight: "500",
                }}
              >
                Zip/Postal Code
              </label>
              <input
                id="zipCode"
                type="text"
                name="zipCode"
                value={team.zipCode}
                onChange={handleChange}
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  fontSize: "1rem",
                  border: "1px solid #e0e0e0",
                  borderRadius: "5px",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05) inset",
                }}
                placeholder="e.g., 90210"
              />
            </div>

            {/* Time Zone */}
            <div style={{ gridColumn: "1 / 3" }}>
              <label 
                htmlFor="timeZone"
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  color: "#444",
                  fontSize: "0.95rem",
                  fontWeight: "500",
                }}
              >
                Time Zone
              </label>
              <select
                id="timeZone"
                name="timeZone"
                value={team.timeZone}
                onChange={handleChange}
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  fontSize: "1rem",
                  border: "1px solid #e0e0e0",
                  borderRadius: "5px",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05) inset",
                  backgroundColor: "#fff",
                }}
              >
                <option value="">Select time zone</option>
                {timeZoneOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: "2rem" }}>
          <button
            type="button"
            onClick={() => navigate("/my-teams")}
            style={{
              padding: "0.75rem 1.5rem",
              fontSize: "1rem",
              border: "1px solid #e0e0e0",
              borderRadius: "5px",
              backgroundColor: "#f5f5f5",
              color: "#555",
              cursor: "pointer",
              transition: "background 0.3s",
            }}
          >
            Cancel
          </button>
          
          <button
            type="submit"
            disabled={saving}
            style={{
              padding: "0.75rem 1.5rem",
              fontSize: "1rem",
              border: "none",
              borderRadius: "5px",
              backgroundColor: "#1976D2",
              color: "#fff",
              cursor: saving ? "not-allowed" : "pointer",
              transition: "background 0.3s, transform 0.2s",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              opacity: saving ? 0.8 : 1,
            }}
          >
            {saving ? (
              <>
                <div 
                  className="spinner-small" 
                  style={{
                    width: "16px",
                    height: "16px",
                    border: "2px solid rgba(255, 255, 255, 0.3)",
                    borderLeft: "2px solid #fff",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                  }}
                ></div>
                Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default EditTeamsPage;